/**
 * Test Cases Page - Tree View with Folder Hierarchy
 */

import React, { useState, useEffect, useRef } from 'react';
import { apiClient, TestCase, Folder } from '../services/api';
import { FolderTree } from '../components/FolderTree';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';
import { TestCaseImport } from '../components/TestCaseImport';
import { ProjectFolderStats } from '../components/ProjectFolderStats';
import './TestCasesPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

type Priority = 'Critical' | 'High' | 'Medium' | 'Low';

const PREDEFINED_TAGS = [
  'Regression', 'Smoke', 'Sanity', 'Integration', 'E2E',
  'API', 'UI', 'Performance', 'Security', 'Functional'
];

interface TestCaseFormData {
  test_case_description: string;
  preconditions: string;
  priority: Priority;
  steps: string;
  expected_result: string;
  tags: string[];
}

interface FolderFormData {
  name: string;
  description: string;
}

export const TestCasesPage: React.FC = () => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('tc-sidebar-open');
    return saved === null ? true : saved === 'true';
  });
  
  const [showTestCaseForm, setShowTestCaseForm] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [folderFormParentId, setFolderFormParentId] = useState<string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [selectedTestCases, setSelectedTestCases] = useState<Set<string>>(new Set());
  
  const [testCaseFormData, setTestCaseFormData] = useState<TestCaseFormData>({
    test_case_description: '',
    preconditions: '',
    priority: 'Medium',
    steps: '',
    expected_result: '',
    tags: []
  });
  
  const [folderFormData, setFolderFormData] = useState<FolderFormData>({
    name: '',
    description: ''
  });
  
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'id', label: 'ID', visible: true, required: true },
    { key: 'description', label: 'Description', visible: true, required: true },
    { key: 'priority', label: 'Priority', visible: true },
    { key: 'tags', label: 'Tags', visible: true },
    { key: 'preconditions', label: 'Preconditions', visible: false },
    { key: 'steps', label: 'Steps', visible: false },
    { key: 'expected', label: 'Expected Result', visible: true },
    { key: 'created_date', label: 'Created Date', visible: true },
    { key: 'created_by', label: 'Created By', visible: false },
    { key: 'actions', label: 'Actions', visible: true, required: true }
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadFolders();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedFolderId) {
      loadTestCases();
    } else {
      setTestCases([]);
    }
  }, [selectedFolderId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const saved = localStorage.getItem('test-cases-columns');
    if (saved) {
      try {
        const visibilityMap = JSON.parse(saved);
        setColumns(cols => cols.map(col => ({
          ...col,
          visible: visibilityMap[col.key] ?? col.visible
        })));
      } catch (e) {
        console.error('Failed to load column preferences:', e);
      }
    }
  }, []);

  const loadFolders = async () => {
    try {
      const data = await apiClient.getFolders('test_case');
      setFolders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    }
  };

  const loadTestCases = async () => {
    if (!selectedFolderId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTestCases(selectedFolderId);
      setTestCases(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const getBreadcrumb = (): Folder[] => {
    if (!selectedFolderId) return [];
    const path: Folder[] = [];
    // eslint-disable-next-line no-loop-func
    let current = folders.find(f => f.id === selectedFolderId);
    while (current) {
      path.unshift(current);
      // eslint-disable-next-line no-loop-func
      current = current.parent_id ? folders.find(f => f.id === current!.parent_id) : undefined;
    }
    return path;
  };

  const handleCreateTestCase = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setEditingTestCase(null);
    setTestCaseFormData({
      test_case_description: '',
      preconditions: '',
      priority: 'Medium',
      steps: '',
      expected_result: '',
      tags: []
    });
    setTagInput('');
    setShowTestCaseForm(true);
  };

  const handleEditTestCase = (testCase: TestCase) => {
    setEditingTestCase(testCase);
    setTestCaseFormData({
      test_case_description: testCase.test_case_description,
      preconditions: testCase.preconditions || '',
      priority: testCase.priority,
      steps: testCase.steps || '',
      expected_result: testCase.expected_result,
      tags: testCase.tags || []
    });
    setTagInput('');
    setShowTestCaseForm(true);
  };

  const handleSubmitTestCase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolderId) return;
    setIsLoading(true);
    setError(null);
    try {
      if (editingTestCase) {
        await apiClient.updateTestCase(editingTestCase.id, testCaseFormData);
      } else {
        await apiClient.createTestCaseManually({
          folder_id: selectedFolderId,
          ...testCaseFormData
        });
      }
      setShowTestCaseForm(false);
      setEditingTestCase(null);
      await loadTestCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save test case');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTestCase = async (caseId: string) => {
    if (!window.confirm('Are you sure you want to delete this test case?')) return;
    try {
      await apiClient.deleteTestCase(caseId);
      await loadTestCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete test case');
    }
  };

  const handleBulkDeleteTestCases = async () => {
    if (selectedTestCases.size === 0) {
      setError('Please select at least one test case to delete');
      return;
    }

    const count = selectedTestCases.size;
    if (!window.confirm(`Are you sure you want to delete ${count} selected test case(s)? This cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const caseIds = Array.from(selectedTestCases);
      const result = await apiClient.bulkDeleteTestCases(caseIds);
      
      if (result.failed_ids.length > 0) {
        setError(`Deleted ${result.deleted_count} of ${result.total_requested} test cases. ${result.failed_ids.length} failed.`);
      }
      
      setSelectedTestCases(new Set()); // Clear selection
      await loadTestCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSubfolder = (parentId: string) => {
    setFolderFormParentId(parentId);
    setFolderFormData({ name: '', description: '' });
    setShowFolderForm(true);
  };

  const handleSubmitFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.createFolder({
        name: folderFormData.name,
        description: folderFormData.description,
        parent_id: folderFormParentId || undefined,
        folder_type: 'test_case'
      });
      setShowFolderForm(false);
      setFolderFormParentId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameFolder = (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (folder) {
      setRenamingFolderId(folderId);
      setFolderFormData({ name: folder.name, description: folder.description || '' });
      setShowRenameForm(true);
    }
  };

  const handleSubmitRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renamingFolderId) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.updateFolder(renamingFolderId, {
        name: folderFormData.name,
        description: folderFormData.description
      });
      setShowRenameForm(false);
      setRenamingFolderId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;
    const hasChildren = folders.some(f => f.parent_id === folderId);
    const message = hasChildren
      ? `Delete "${folder.name}" and all its subfolders and test cases? This cannot be undone.`
      : `Delete "${folder.name}" and all its test cases? This cannot be undone.`;
    if (!window.confirm(message)) return;
    try {
      await apiClient.deleteFolder(folderId);
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null);
      }
      await loadFolders();
    } catch (err: any) {
      // Handle specific error for folders with content
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to delete folder';
      setError(errorMessage);
    }
  };

  const handleToggleTestCase = (testCaseId: string) => {
    const newSelected = new Set(selectedTestCases);
    if (newSelected.has(testCaseId)) {
      newSelected.delete(testCaseId);
    } else {
      newSelected.add(testCaseId);
    }
    setSelectedTestCases(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTestCases.size === filteredTestCases.length) {
      setSelectedTestCases(new Set());
    } else {
      setSelectedTestCases(new Set(filteredTestCases.map(tc => tc.id)));
    }
  };

  const handleImportExcel = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setShowImportModal(true);
  };

  const handleImportComplete = async (_importedTestCases: TestCase[]) => {
    setShowImportModal(false);
    // Reload folders (XML import creates subfolders) then reload test cases
    const currentFolderId = selectedFolderId;
    try {
      const updatedFolders = await apiClient.getFolders('test_case');
      setFolders(updatedFolders);
    } catch (e) { /* ignore */ }
    // Explicitly reload test cases for the current folder
    if (currentFolderId) {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiClient.getTestCases(currentFolderId);
        setTestCases(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load test cases');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExportTestCases = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    
    if (selectedTestCases.size === 0) {
      setError('Please select at least one test case to export');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get token from auth_tokens (same as api.ts)
      const authTokens = localStorage.getItem('auth_tokens');
      if (!authTokens) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      const tokens = JSON.parse(authTokens);
      const accessToken = tokens.access_token;
      
      // Use POST endpoint with proper structure
      const response = await fetch(`${API_BASE_URL}/api/v1/export/test-cases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          format: 'excel',
          scope: 'test_cases',
          folder_id: selectedFolderId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.status} - ${errorText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test_cases_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSelectedTestCases(new Set()); // Clear selection after export
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareLink = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    try {
      const folder = folders.find(f => f.id === selectedFolderId);
      if (folder?.link_token) {
        const link = `${window.location.origin}/shared/${folder.link_token}`;
        setShareLink(link);
        setShowShareModal(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get share link');
    }
  };

  const handleCreateTestRun = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    window.location.href = `/test-runs/create?folder_id=${selectedFolderId}&type=test_case`;
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !testCaseFormData.tags.includes(tag)) {
      setTestCaseFormData({ ...testCaseFormData, tags: [...testCaseFormData.tags, tag] });
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTestCaseFormData({
      ...testCaseFormData,
      tags: testCaseFormData.tags.filter(t => t !== tagToRemove)
    });
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSelectSuggestion = (tag: string) => {
    if (!testCaseFormData.tags.includes(tag)) {
      setTestCaseFormData({ ...testCaseFormData, tags: [...testCaseFormData.tags, tag] });
    }
    setTagInput('');
    setShowTagSuggestions(false);
    tagInputRef.current?.focus();
  };

  const filteredSuggestions = PREDEFINED_TAGS.filter(
    tag => tag.toLowerCase().includes(tagInput.toLowerCase()) && !testCaseFormData.tags.includes(tag)
  );

  const filteredTestCases = searchTerm
    ? testCases.filter(tc =>
        tc.test_case_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tc.test_case_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tc.expected_result && tc.expected_result.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (tc.tags && tc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      )
    : testCases;

  const getPriorityColor = (priority: Priority): string => {
    switch (priority) {
      case 'Critical': return '#dc3545';
      case 'High': return '#fd7e14';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => {
      localStorage.setItem('tc-sidebar-open', String(!prev));
      return !prev;
    });
  };

  const breadcrumb = getBreadcrumb();

  return (
    <div className="test-cases-page-tree">
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}
      <div className="test-cases-layout">
        <div className={`test-cases-sidebar${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
          <div className="sidebar-inner">
            <FolderTree
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
            onCreateSubfolder={handleCreateSubfolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            title="Test Cases"
          />
          </div>
          <button className="sidebar-toggle-btn" onClick={toggleSidebar} title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            {sidebarOpen ? '‹' : '›'}
          </button>
        </div>
        <div className="test-cases-content">
          {selectedFolderId ? (
            <>
              <div className="content-breadcrumb">
                <span className="breadcrumb-icon">📍</span>
                {breadcrumb.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    {index > 0 && <span className="breadcrumb-separator">›</span>}
                    <span className="breadcrumb-item">{folder.name}</span>
                  </React.Fragment>
                ))}
              </div>
              <div className="content-actions">
                <button className="btn btn-secondary" onClick={() => handleCreateSubfolder(selectedFolderId)}>
                  <span>📁</span> Create Subfolder
                </button>
                {folders.find(f => f.id === selectedFolderId)?.level !== 0 && (
                  <button className="btn btn-primary" onClick={handleCreateTestCase}>
                    <span>➕</span> Create Test Case
                  </button>
                )}
              </div>

              {/* Action bar — Import always visible; rest only for non-root folders */}
              {(() => {
                const isProjectFolder = folders.find(f => f.id === selectedFolderId)?.level === 0;
                return (
                  <div className="action-bar">
                    <button className="btn btn-action" onClick={handleImportExcel} title="Import test cases from Excel or XML">
                      <span>📥</span> Import
                    </button>
                    {!isProjectFolder && (
                      <>
                        <button
                          className="btn btn-action"
                          onClick={handleExportTestCases}
                          disabled={selectedTestCases.size === 0}
                          title={selectedTestCases.size === 0 ? "Select test cases to export" : `Export ${selectedTestCases.size} selected test case(s)`}
                        >
                          <span>📤</span> Export ({selectedTestCases.size})
                        </button>
                        <button
                          className="btn btn-action btn-delete"
                          onClick={handleBulkDeleteTestCases}
                          disabled={selectedTestCases.size === 0}
                          title={selectedTestCases.size === 0 ? "Select test cases to delete" : `Delete ${selectedTestCases.size} selected test case(s)`}
                        >
                          <span>🗑️</span> Delete Selected ({selectedTestCases.size})
                        </button>
                        <button className="btn btn-action" onClick={handleShareLink} title="Get shareable link for this folder">
                          <span>🔗</span> Share Link
                        </button>
                        <button className="btn btn-action" onClick={handleCreateTestRun} title="Create test run from test cases">
                          <span>▶️</span> Create Test Run
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Root folder: stats only. Sub-folders: full table */}
              {(() => {
                const isProjectFolder = folders.find(f => f.id === selectedFolderId)?.level === 0;

                if (isProjectFolder) {
                  return <ProjectFolderStats folderId={selectedFolderId} folderType="test_case" />;
                }

                return (
                  <>
                    <div className="content-controls">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search test cases..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <ColumnSelector
                        columns={columns}
                        onColumnsChange={setColumns}
                        storageKey="test-cases-columns"
                      />
                      <div className="stats-badge">
                        Total: <strong>{testCases.length}</strong>
                      </div>
                      {searchTerm && (
                        <div className="stats-badge filtered">
                          Filtered: <strong>{filteredTestCases.length}</strong>
                        </div>
                      )}
                    </div>
                    {isLoading ? (
                      <div className="content-loading">Loading test cases...</div>
                    ) : filteredTestCases.length === 0 ? (
                      <div className="content-empty">
                        <p>{searchTerm ? 'No test cases match your search' : 'No test cases in this folder'}</p>
                        <button className="btn btn-primary" onClick={handleCreateTestCase}>
                          Create First Test Case
                        </button>
                      </div>
                    ) : (
                      <div className="test-cases-table-container">
                        <table className="test-cases-table">
                          <thead>
                            <tr>
                              <th className="col-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedTestCases.size === filteredTestCases.length && filteredTestCases.length > 0}
                                  onChange={handleSelectAll}
                                  title="Select all"
                                />
                              </th>
                              {columns.filter(col => col.visible).map(col => (
                                <th key={col.key} className={`col-${col.key}`}>{col.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTestCases.map(testCase => (
                              <tr key={testCase.id} className={selectedTestCases.has(testCase.id) ? 'selected-row' : ''}>
                                <td className="col-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedTestCases.has(testCase.id)}
                                    onChange={() => handleToggleTestCase(testCase.id)}
                                  />
                                </td>
                                {columns.find(c => c.key === 'id')?.visible && (
                                  <td className="col-id">
                                    <span className="test-case-id">{testCase.test_case_id}</span>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'description')?.visible && (
                                  <td className="col-description">
                                    <strong>{testCase.test_case_description}</strong>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'priority')?.visible && (
                                  <td className="col-priority">
                                    <span className="priority-badge" style={{ backgroundColor: getPriorityColor(testCase.priority) }}>
                                      {testCase.priority}
                                    </span>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'tags')?.visible && (
                                  <td className="col-tags">
                                    {testCase.tags && testCase.tags.length > 0 ? (
                                      <div className="tags-cell">
                                        {testCase.tags.map(tag => (
                                          <span key={tag} className="tag-badge">{tag}</span>
                                        ))}
                                      </div>
                                    ) : <span className="no-data">—</span>}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'preconditions')?.visible && (
                                  <td className="col-preconditions">
                                    {testCase.preconditions || <span className="no-data">—</span>}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'steps')?.visible && (
                                  <td className="col-steps">
                                    {testCase.steps ? <div className="steps-cell">{testCase.steps}</div> : <span className="no-data">—</span>}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'expected')?.visible && (
                                  <td className="col-expected">
                                    <div className="expected-cell">
                                      {testCase.expected_result
                                        ? (() => {
                                            const cleaned = testCase.expected_result
                                              .replace(/!\[.*?\]\([^)]*\)/g, '')
                                              .replace(/\[.*?\]\([^)]*\)/g, '')
                                              .replace(/https?:\/\/\S+/g, '')
                                              .replace(/index\.php[^\s)]*/g, '')
                                              .trim();
                                            return cleaned || <span className="no-data">—</span>;
                                          })()
                                        : <span className="no-data">—</span>
                                      }
                                    </div>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'created_date')?.visible && (
                                  <td className="col-created-date">
                                    {new Date(testCase.created_at).toLocaleDateString()}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'created_by')?.visible && (
                                  <td className="col-created-by">{testCase.created_by}</td>
                                )}
                                {columns.find(c => c.key === 'actions')?.visible && (
                                  <td className="col-actions">
                                    <div className="action-buttons">
                                      <button className="action-btn edit" onClick={() => handleEditTestCase(testCase)} title="Edit">✏️</button>
                                      <button className="action-btn delete" onClick={() => handleDeleteTestCase(testCase.id)} title="Delete">🗑️</button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <div className="content-empty-state">
              <div className="empty-state-icon">📁</div>
              <h3>Select a Folder</h3>
              <p>Choose a folder from the tree to view and manage test cases</p>
            </div>
          )}
        </div>
      </div>
      {showTestCaseForm && (
        <div className="modal-overlay" onClick={() => setShowTestCaseForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTestCase ? 'Edit Test Case' : 'Create Test Case'}</h3>
              <button onClick={() => setShowTestCaseForm(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmitTestCase} className="modal-form">
              <div className="form-group">
                <label>Description <span className="required">*</span></label>
                <input type="text" className="form-control" value={testCaseFormData.test_case_description}
                  onChange={(e) => setTestCaseFormData({ ...testCaseFormData, test_case_description: e.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Priority <span className="required">*</span></label>
                  <select className="form-control" value={testCaseFormData.priority}
                    onChange={(e) => setTestCaseFormData({ ...testCaseFormData, priority: e.target.value as Priority })} required>
                    <option value="Critical">Critical</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Preconditions</label>
                <textarea className="form-control" value={testCaseFormData.preconditions}
                  onChange={(e) => setTestCaseFormData({ ...testCaseFormData, preconditions: e.target.value })} rows={2} />
              </div>
              <div className="form-group">
                <label>Steps</label>
                <textarea className="form-control" value={testCaseFormData.steps}
                  onChange={(e) => setTestCaseFormData({ ...testCaseFormData, steps: e.target.value })} rows={4}
                  placeholder="1. First step&#10;2. Second step&#10;3. Third step" />
              </div>
              <div className="form-group">
                <label>Expected Result <span className="required">*</span></label>
                <textarea className="form-control" value={testCaseFormData.expected_result}
                  onChange={(e) => setTestCaseFormData({ ...testCaseFormData, expected_result: e.target.value })} rows={3} required />
              </div>
              <div className="form-group">
                <label>Tags</label>
                <div className="tags-input-container">
                  <div className="tags-input-wrapper">
                    <input ref={tagInputRef} type="text" className="tags-input" value={tagInput}
                      onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(e.target.value.length > 0); }}
                      onKeyDown={handleTagInputKeyDown}
                      onFocus={() => setShowTagSuggestions(tagInput.length > 0)}
                      onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                      placeholder="Add a tag" />
                    <button type="button" className="btn-add-tag" onClick={handleAddTag}>Add Tag</button>
                  </div>
                  {showTagSuggestions && filteredSuggestions.length > 0 && (
                    <div className="tag-suggestions">
                      {filteredSuggestions.map(tag => (
                        <div key={tag} className="tag-suggestion" onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(tag); }}>{tag}</div>
                      ))}
                    </div>
                  )}
                  {testCaseFormData.tags.length > 0 && (
                    <div className="selected-tags">
                      {testCaseFormData.tags.map(tag => (
                        <span key={tag} className="selected-tag">
                          {tag}
                          <button type="button" className="remove-tag" onClick={() => handleRemoveTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : editingTestCase ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTestCaseForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showFolderForm && (
        <div className="modal-overlay" onClick={() => setShowFolderForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Subfolder</h3>
              <button onClick={() => setShowFolderForm(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmitFolder} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })} required autoFocus />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })} rows={2} />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowFolderForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showRenameForm && (
        <div className="modal-overlay" onClick={() => setShowRenameForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rename Folder</h3>
              <button onClick={() => setShowRenameForm(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmitRename} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })} required autoFocus />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })} rows={2} />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRenameForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Folder Link</h3>
              <button onClick={() => setShowShareModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p>Share this link to give others access to this folder:</p>
              <div className="share-link-container">
                <input 
                  type="text" 
                  className="form-control" 
                  value={shareLink} 
                  readOnly 
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink);
                    alert('Link copied to clipboard!');
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && selectedFolderId && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <TestCaseImport
              folderId={selectedFolderId}
              onImportComplete={handleImportComplete}
              onCancel={() => setShowImportModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
